import { useMemo, useState } from 'react';
import {
  requiresDateRangeForAccountingExportItems,
} from '../Service/accountingExportApi.contract';
import type { AccountingExportItemsQuery } from '../Types/accountingExportTypes';

export type AccountingExportSentFilter = '' | 'true' | 'false';
export type AccountingExportChannelFormFilter = '' | 'sales' | 'purchases';
export type AccountingExportSourceFormFilter = '' | 'booking' | 'standalone';

export interface AccountingExportFormFilters {
  dateFrom: string;
  dateTo: string;
  companyId: string;
  source: AccountingExportSourceFormFilter;
  channel: AccountingExportChannelFormFilter;
  sentToAccounting: AccountingExportSentFilter;
}

export interface FeedbackState {
  severity: 'success' | 'warning' | 'error';
  message: string;
}

export const defaultAccountingExportFormFilters: AccountingExportFormFilters = {
  dateFrom: '',
  dateTo: '',
  companyId: '',
  source: '',
  channel: '',
  sentToAccounting: '',
};

const getSentToAccountingValue = (value: AccountingExportSentFilter): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const toAccountingExportItemsQuery = (filters: AccountingExportFormFilters): AccountingExportItemsQuery => ({
  companyId: Number(filters.companyId),
  dateFrom: filters.dateFrom || undefined,
  dateTo: filters.dateTo || undefined,
  source: filters.source || undefined,
  channel: filters.channel || undefined,
  sentToAccounting: getSentToAccountingValue(filters.sentToAccounting),
});

const areFiltersEqual = (
  a: AccountingExportFormFilters,
  b: AccountingExportFormFilters
): boolean =>
  a.dateFrom === b.dateFrom &&
  a.dateTo === b.dateTo &&
  a.companyId === b.companyId &&
  a.source === b.source &&
  a.channel === b.channel &&
  a.sentToAccounting === b.sentToAccounting;

export const getFilterValidationError = (filters: AccountingExportFormFilters): string => {
  if (!filters.companyId.trim()) {
    return 'Spółka jest wymagana do pobrania listy i eksportów księgowych.';
  }

  if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
    return "Nieprawidłowy zakres dat: 'od' nie może być późniejsze niż 'do'.";
  }

  const listQuery = toAccountingExportItemsQuery(filters);
  if (
    requiresDateRangeForAccountingExportItems(listQuery) &&
    (!filters.dateFrom.trim() || !filters.dateTo.trim())
  ) {
    return 'Dla zakresu obejmującego sprzedaż standalone podaj pełny zakres dat od/do.';
  }

  return '';
};

export const getExportPreconditionFeedback = (filters: AccountingExportFormFilters): FeedbackState | null => {
  if (!filters.companyId.trim()) {
    return { severity: 'error', message: 'Najpierw wybierz spółkę i zastosuj filtry.' };
  }
  if (!filters.dateFrom.trim() || !filters.dateTo.trim()) {
    return { severity: 'error', message: 'Aby wygenerować eksport, wybierz pełny zakres dat od/do i zastosuj filtry.' };
  }
  return null;
};

export interface UseAccountingExportFiltersReturn {
  filters: AccountingExportFormFilters;
  validationError: string;
  feedback: FeedbackState | null;
  setFeedback: (feedback: FeedbackState | null) => void;
  hasValidAppliedFilters: boolean;
  listQuery: AccountingExportItemsQuery;
  exportPreconditionFeedback: FeedbackState | null;
  /** Called by the consumer when submitted filters are identical to the active ones. */
  handleSearch: (values: Record<string, string>, onSameFilters?: () => void) => void;
  handleReset: () => void;
}

export const useAccountingExportFilters = (): UseAccountingExportFiltersReturn => {
  const [filters, setFilters] = useState<AccountingExportFormFilters>(defaultAccountingExportFormFilters);
  const [validationError, setValidationError] = useState('');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  const hasValidAppliedFilters = filters.companyId.trim().length > 0;
  const listQuery = useMemo(() => toAccountingExportItemsQuery(filters), [filters]);
  const exportPreconditionFeedback = getExportPreconditionFeedback(filters);

  const handleSearch = (values: Record<string, string>, onSameFilters?: () => void) => {
    const nextFilters: AccountingExportFormFilters = {
      dateFrom: values.dateFrom ?? '',
      dateTo: values.dateTo ?? '',
      companyId: values.companyId ?? '',
      source: values.source === 'booking' || values.source === 'standalone' ? values.source : '',
      channel: values.channel === 'sales' || values.channel === 'purchases' ? values.channel : '',
      sentToAccounting:
        values.sentToAccounting === 'true' || values.sentToAccounting === 'false'
          ? values.sentToAccounting
          : '',
    };

    const nextValidationError = getFilterValidationError(nextFilters);
    if (nextValidationError) {
      setFeedback(null);
      setValidationError(nextValidationError);
      return;
    }

    setValidationError('');
    setFeedback(null);

    if (areFiltersEqual(filters, nextFilters)) {
      if (hasValidAppliedFilters) {
        onSameFilters?.();
      }
      return;
    }

    setFilters(nextFilters);
  };

  const handleReset = () => {
    setValidationError('');
    setFeedback(null);
    setFilters(defaultAccountingExportFormFilters);
  };

  return {
    filters,
    validationError,
    feedback,
    setFeedback,
    hasValidAppliedFilters,
    listQuery,
    exportPreconditionFeedback,
    handleSearch,
    handleReset,
  };
};
